/** Max edge length for booking photos (upload + lighter decode in the gallery). */
export const BOOKING_IMAGE_MAX_EDGE = 1920
export const BOOKING_IMAGE_JPEG_QUALITY = 0.85

const SKIP_TYPES = new Set(["image/gif", "image/svg+xml"])

const scaleToFit = (
	width: number,
	height: number,
	maxWidth: number,
	maxHeight: number
): { width: number; height: number } => {
	if (width <= maxWidth && height <= maxHeight) {
		return { width, height }
	}
	const ratio = Math.min(maxWidth / width, maxHeight / height)
	return {
		width: Math.max(1, Math.round(width * ratio)),
		height: Math.max(1, Math.round(height * ratio)),
	}
}

const canvasToBlob = (
	canvas: HTMLCanvasElement,
	type: string,
	quality: number
): Promise<Blob | null> =>
	new Promise(resolve => canvas.toBlob(resolve, type, quality))

/**
 * Downscales and re-encodes an image file before upload so uploads are faster
 * and the gallery does not decode multi‑MB originals in the browser.
 */
export const compressImageForUpload = async (
	file: File,
	options: {
		maxWidth?: number
		maxHeight?: number
		quality?: number
		mimeType?: string
	} = {}
): Promise<File> => {
	const maxWidth = options.maxWidth ?? BOOKING_IMAGE_MAX_EDGE
	const maxHeight = options.maxHeight ?? BOOKING_IMAGE_MAX_EDGE
	const quality = options.quality ?? BOOKING_IMAGE_JPEG_QUALITY
	const mimeType = options.mimeType ?? "image/jpeg"

	if (!file.type.startsWith("image/") || SKIP_TYPES.has(file.type)) {
		return file
	}

	// Already small enough — skip re-encode to save CPU
	if (file.size <= 400_000) {
		return file
	}

	let bitmap: ImageBitmap | null = null
	try {
		bitmap = await createImageBitmap(file)
		const { width, height } = scaleToFit(bitmap.width, bitmap.height, maxWidth, maxHeight)

		if (
			width === bitmap.width &&
			height === bitmap.height &&
			file.type === mimeType &&
			file.size <= 1_500_000
		) {
			bitmap.close()
			return file
		}

		const canvas = document.createElement("canvas")
		canvas.width = width
		canvas.height = height
		const ctx = canvas.getContext("2d")
		if (!ctx) {
			bitmap.close()
			return file
		}
		ctx.drawImage(bitmap, 0, 0, width, height)
		bitmap.close()
		bitmap = null

		const blob = await canvasToBlob(canvas, mimeType, quality)
		if (!blob) {
			return file
		}

		const baseName = file.name.replace(/\.[^.]+$/i, "") || "photo"
		const ext = mimeType === "image/webp" ? "webp" : "jpg"
		return new File([blob], `${baseName}.${ext}`, {
			type: mimeType,
			lastModified: Date.now(),
		})
	} catch {
		bitmap?.close()
		return file
	}
}
